import React, { useMemo, useState } from 'react';
import { Card, Select } from 'antd';
import ClipLoader from 'react-spinners/ClipLoader';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { MonthlyPaymentComparisonData } from '@models/statistics';

// Chart.js 등록
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const { Option } = Select;

interface MonthlyPaymentComparisonChartProps {
  data: MonthlyPaymentComparisonData[];
  isLoading: boolean;
}

export const MonthlyPaymentComparisonChart: React.FC<MonthlyPaymentComparisonChartProps> = ({ data, isLoading }) => {
  const [viewMode, setViewMode] = useState<'amount' | 'count'>('amount');

  // 차트 데이터 생성
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // 월 라벨 생성 (YY년 MMM 형식: 24년 1월, 24년 2월, ...)
    const monthLabels = data.map((item) => {
      const [year, month] = item.monthString.split('-');
      const shortYear = year.slice(-2); // 마지막 2자리만 (2024 → 24)
      const date = new Date(parseInt(year), parseInt(month) - 1);
      const monthName = date.toLocaleDateString('ko-KR', { month: 'short' });
      return `${shortYear}년 ${monthName}`;
    });

    // 현재년도와 전년도 데이터셋
    const currentYearData = data.map((item) =>
      viewMode === 'amount' ? item.currentYear.totalAmount : item.currentYear.count,
    );
    const previousYearData = data.map((item) =>
      viewMode === 'amount' ? item.previousYear.totalAmount : item.previousYear.count,
    );

    // 연도 정보는 위에서 이미 계산됨

    const datasets = [
      {
        label: `지난 12개월`,
        data: currentYearData,
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F620',
        borderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: '#3B82F6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.2,
        fill: false,
      },
      {
        label: `전년 동기`,
        data: previousYearData,
        borderColor: '#EF4444',
        backgroundColor: '#EF444420',
        borderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: '#EF4444',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.2,
        fill: false,
        borderDash: [5, 5], // 점선으로 전년도 구분
      },
    ];

    return {
      labels: monthLabels,
      datasets,
    };
  }, [data, viewMode]);

  // Chart.js 옵션
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            const dataPoint = data[context.dataIndex];
            const suffix = viewMode === 'amount' ? '원' : '건';

            if (viewMode === 'amount') {
              const formattedValue = value.toLocaleString();
              const growth = context.datasetIndex === 0 ? dataPoint.growth.amountGrowthRate : 0;
              const growthText =
                context.datasetIndex === 0 && dataPoint.previousYear.totalAmount > 0
                  ? ` (${growth > 0 ? '+' : ''}${growth}%)`
                  : '';
              return `${context.dataset.label}: ${formattedValue}${suffix}${growthText}`;
            } else {
              const growth = context.datasetIndex === 0 ? dataPoint.growth.countGrowthRate : 0;
              const growthText =
                context.datasetIndex === 0 && dataPoint.previousYear.count > 0
                  ? ` (${growth > 0 ? '+' : ''}${growth}%)`
                  : '';
              return `${context.dataset.label}: ${value}${suffix}${growthText}`;
            }
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: '월',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        grid: {
          display: true,
          color: '#f0f0f0',
        },
      },
      y: {
        title: {
          display: true,
          text: viewMode === 'amount' ? '결제 금액 (원)' : '결제 건수',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        beginAtZero: true,
        grid: {
          display: true,
          color: '#f0f0f0',
        },
        ticks: {
          callback: function (value) {
            if (viewMode === 'amount') {
              return (value as number).toLocaleString();
            }
            return value;
          },
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      point: {
        hoverRadius: 10,
      },
    },
  };

  const totalCurrentYear = data.reduce((acc, item) => acc + item.currentYear.totalAmount, 0);
  const totalPreviousYear = data.reduce((acc, item) => acc + item.previousYear.totalAmount, 0);
  const totalCurrentYearCount = data.reduce((acc, item) => acc + item.currentYear.count, 0);
  const totalPreviousYearCount = data.reduce((acc, item) => acc + item.previousYear.count, 0);

  const chartTitle = (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
      <div>월별 결제 금액</div>
      <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
        {viewMode === 'amount' && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
            <div>
              {`${totalCurrentYear.toLocaleString()}원(`}
              <span style={{ color: totalCurrentYear >= totalPreviousYear ? '#ef4444' : '#3b82f6' }}>
                {`${((totalCurrentYear / totalPreviousYear) * 100).toFixed(1)}%`}
              </span>
              {`)`}
            </div>
          </div>
        )}
        {viewMode === 'count' && (
          <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
              }}>
              {`${totalCurrentYearCount.toLocaleString()}건(`}
              <span style={{ color: totalCurrentYearCount >= totalPreviousYearCount ? '#ef4444' : '#3b82f6' }}>
                {`${((totalCurrentYearCount / totalPreviousYearCount) * 100).toFixed(1)}%`}
              </span>
              {`)`}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Card
        className="chart"
        title={chartTitle}
        bordered={false}
        style={{ maxWidth: 'calc(50% - 6px)', minWidth: 'calc(50% - 6px)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ClipLoader />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="chart"
      title={chartTitle}
      bordered={false}
      style={{ maxWidth: 'calc(50% - 6px)', minWidth: 'calc(50% - 6px)' }}
      extra={
        <Select value={viewMode} onChange={setViewMode} style={{ width: 120 }} size="small">
          <Option value="amount">결제 금액</Option>
          <Option value="count">결제 건수</Option>
        </Select>
      }>
      <div style={{ height: '400px', padding: '0' }}>
        {data && data.length > 0 ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#999',
            }}>
            결제 비교 데이터가 없습니다.
          </div>
        )}
      </div>
    </Card>
  );
};

export default MonthlyPaymentComparisonChart;
